import { useIncidentAttackPaths, useIncidentNarrative } from '@/hooks/use-attack-paths';
import { get, post } from '@/lib/api';
import {
	ATTACK_PATH_SCORE_COLORS,
	INCIDENT_STATUS_COLORS,
	INCIDENT_STATUS_LABELS,
	formatTimestamp,
	getScoreLevel,
} from '@/lib/constants';
import { queryKeys } from '@/lib/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EvidenceRefInfo, IncidentDetail, IncidentTimelineEvent } from '@uncain/shared';
import { useState } from 'react';
import { useParams } from 'react-router';

export function IncidentDetailPage() {
	const { id } = useParams<{ id: string }>();
	const queryClient = useQueryClient();
	const [approveReason, setApproveReason] = useState('');
	const [dismissReason, setDismissReason] = useState('');
	const incidentId = id ?? '';

	const { data: detail, isLoading } = useQuery<IncidentDetail>({
		queryKey: queryKeys.incidents.detail(incidentId),
		queryFn: () => get<IncidentDetail>(`/incidents/${incidentId}`),
		enabled: !!id,
		refetchInterval: 5 * 1000,
	});

	const timeline = useQuery<{ timeline: IncidentTimelineEvent[] }>({
		queryKey: queryKeys.incidents.timeline(incidentId),
		queryFn: () => get<{ timeline: IncidentTimelineEvent[] }>(`/incidents/${incidentId}/timeline`),
		enabled: !!id,
	});

	const evidence = useQuery<{ evidence: EvidenceRefInfo[] }>({
		queryKey: queryKeys.incidents.evidence(incidentId),
		queryFn: () => get<{ evidence: EvidenceRefInfo[] }>(`/incidents/${incidentId}/evidence`),
		enabled: !!id,
	});

	const narrative = useIncidentNarrative(incidentId);
	const attackPaths = useIncidentAttackPaths(incidentId);

	const approve = useMutation({
		mutationFn: () => post(`/incidents/${incidentId}/approve`, { reason: approveReason }),
		onSuccess: () => {
			setApproveReason('');
			queryClient.invalidateQueries({ queryKey: queryKeys.incidents.detail(incidentId) });
			queryClient.invalidateQueries({ queryKey: queryKeys.incidents.all });
		},
	});

	const dismiss = useMutation({
		mutationFn: () => post(`/incidents/${incidentId}/dismiss`, { reason: dismissReason }),
		onSuccess: () => {
			setDismissReason('');
			queryClient.invalidateQueries({ queryKey: queryKeys.incidents.detail(incidentId) });
			queryClient.invalidateQueries({ queryKey: queryKeys.incidents.all });
		},
	});

	if (isLoading) {
		return <div className="text-muted-foreground">Loading incident...</div>;
	}

	if (!detail) {
		return <div className="text-muted-foreground">Incident not found.</div>;
	}

	const { incident, customerBlastRadius, approvalGates } = detail;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-start justify-between">
				<div className="space-y-1">
					<div className="flex items-center gap-2">
						<h1 className="text-2xl font-bold text-foreground">
							{incident.summary || `Incident ${incident.id.slice(0, 8)}`}
						</h1>
						<span
							className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
								INCIDENT_STATUS_COLORS[incident.status] ?? 'bg-gray-100 text-gray-800'
							}`}
						>
							{INCIDENT_STATUS_LABELS[incident.status] ?? incident.status}
						</span>
					</div>
					<p className="text-sm text-muted-foreground">
						{incident.upstreamRepo} &middot; {incident.eventType} &middot; Created{' '}
						{formatTimestamp(incident.createdAt)}
					</p>
				</div>
			</div>

			{/* Blast radius */}
			{customerBlastRadius && (
				<div className="rounded-lg border border-border bg-card p-5">
					<h2 className="text-lg font-semibold text-foreground">Blast Radius</h2>
					<div className="mt-3 grid grid-cols-3 gap-4 text-sm">
						<div>
							<span className="text-muted-foreground">Score:</span>{' '}
							<span className="font-medium">{customerBlastRadius.blastRadiusScore}</span>
						</div>
						<div>
							<span className="text-muted-foreground">Release path:</span>{' '}
							<span className="font-medium">
								{customerBlastRadius.hasReleasePath ? 'Yes' : 'No'}
							</span>
						</div>
						<div>
							<span className="text-muted-foreground">Secrets exposed:</span>{' '}
							<span className="font-medium">{customerBlastRadius.hasSecrets ? 'Yes' : 'No'}</span>
						</div>
					</div>
				</div>
			)}

			{/* Approval gates */}
			{approvalGates.length > 0 && (
				<div className="rounded-lg border border-border bg-card p-5">
					<h2 className="text-lg font-semibold text-foreground">Approval Gates</h2>
					<div className="mt-3 space-y-3">
						{approvalGates.map((gate) => (
							<div
								key={gate.id}
								className="flex items-center justify-between rounded border border-border p-3"
							>
								<div>
									<span className="text-sm font-medium text-foreground">{gate.gateType}</span>
									<span className="ml-2 text-xs text-muted-foreground">{gate.status}</span>
								</div>
								{gate.status === 'pending' && (
									<span className="text-xs text-muted-foreground">
										Timeout: {gate.timeoutSeconds}s
									</span>
								)}
							</div>
						))}
					</div>
				</div>
			)}

			{/* Actions */}
			<div className="flex gap-4">
				<div className="flex-1 rounded-lg border border-border bg-card p-5">
					<h3 className="text-sm font-semibold text-foreground">Approve Containment</h3>
					<textarea
						value={approveReason}
						onChange={(e) => setApproveReason(e.target.value)}
						placeholder="Approval reason..."
						className="mt-2 w-full rounded border border-border bg-background px-3 py-2 text-sm"
						rows={2}
					/>
					<button
						type="button"
						onClick={() => approve.mutate()}
						disabled={!approveReason.trim() || approve.isPending}
						className="mt-2 rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
					>
						{approve.isPending ? 'Approving...' : 'Approve'}
					</button>
				</div>
				<div className="flex-1 rounded-lg border border-border bg-card p-5">
					<h3 className="text-sm font-semibold text-foreground">Dismiss Incident</h3>
					<textarea
						value={dismissReason}
						onChange={(e) => setDismissReason(e.target.value)}
						placeholder="Dismissal reason..."
						className="mt-2 w-full rounded border border-border bg-background px-3 py-2 text-sm"
						rows={2}
					/>
					<button
						type="button"
						onClick={() => dismiss.mutate()}
						disabled={!dismissReason.trim() || dismiss.isPending}
						className="mt-2 rounded bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground disabled:opacity-50"
					>
						{dismiss.isPending ? 'Dismissing...' : 'Dismiss'}
					</button>
				</div>
			</div>

			{/* Timeline */}
			{timeline.data && (
				<div className="rounded-lg border border-border bg-card p-5">
					<h2 className="text-lg font-semibold text-foreground">Timeline</h2>
					<div className="mt-3 space-y-3">
						{timeline.data.timeline.map((event, idx) => (
							<div key={`event-${String(idx)}`} className="flex gap-3 text-sm">
								<span className="shrink-0 text-muted-foreground">
									{formatTimestamp(event.timestamp)}
								</span>
								<span className="text-foreground">{event.description}</span>
							</div>
						))}
					</div>
				</div>
			)}

			{/* AI Narrative */}
			{narrative.data?.narrative && (
				<div className="rounded-lg border border-blue-200 bg-blue-50/50 p-5">
					<div className="flex items-center gap-2">
						<h2 className="text-lg font-semibold text-foreground">Incident Summary</h2>
						{narrative.data.isAiGenerated && (
							<span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
								AI-generated
							</span>
						)}
					</div>
					<p className="mt-3 whitespace-pre-wrap text-sm text-foreground">
						{narrative.data.narrative}
					</p>
				</div>
			)}

			{/* Attack Paths */}
			{attackPaths.data && attackPaths.data.paths.length > 0 && (
				<div className="rounded-lg border border-border bg-card p-5">
					<h2 className="text-lg font-semibold text-foreground">
						Attack Paths ({attackPaths.data.summary.total})
					</h2>
					<div className="mt-3 space-y-2">
						{attackPaths.data.paths.slice(0, 5).map((path) => {
							const level = getScoreLevel(path.score);
							const colorClass = ATTACK_PATH_SCORE_COLORS[level] ?? 'bg-gray-100 text-gray-800';
							return (
								<div
									key={path.id}
									className="flex items-center justify-between rounded border border-border p-3 text-sm"
								>
									<div className="flex items-center gap-3">
										<span
											className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}
										>
											{path.score.toFixed(1)}
										</span>
										<span className="text-foreground">{path.source.label}</span>
										<span className="text-muted-foreground">→</span>
										<span className="text-foreground">{path.target.label}</span>
									</div>
									<span className="text-xs text-muted-foreground">
										{path.stepCount} steps, {path.boundaryCount} boundaries
									</span>
								</div>
							);
						})}
						{attackPaths.data.paths.length > 5 && (
							<p className="text-xs text-muted-foreground">
								+ {attackPaths.data.paths.length - 5} more paths
							</p>
						)}
					</div>
				</div>
			)}

			{/* Evidence */}
			{evidence.data && evidence.data.evidence.length > 0 && (
				<div className="rounded-lg border border-border bg-card p-5">
					<h2 className="text-lg font-semibold text-foreground">Evidence</h2>
					<div className="mt-3 space-y-2">
						{evidence.data.evidence.map((ref) => (
							<div
								key={ref.id}
								className="flex items-center justify-between rounded border border-border p-3 text-sm"
							>
								<div>
									<span className="font-medium text-foreground">{ref.evidenceType}</span>
									<span className="ml-2 font-mono text-xs text-muted-foreground">
										{ref.contentHash.slice(0, 12)}
									</span>
								</div>
								<span className="text-xs text-muted-foreground">
									{(ref.sizeBytes / 1024).toFixed(1)} KB
								</span>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
